'use client';

import Link from 'next/link';

import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

const LANGUAGES = [
  { value: 'th', label: 'Thai' },
  { value: 'en', label: 'English' },
] as const;

export type Language = (typeof LANGUAGES)[number]['value'];

interface LanguageToggleProps {
  value: Language;
}

interface LanguageTabProps {
  item: (typeof LANGUAGES)[number];
  isActive: boolean;
}

const toggleItemVariants = cnva('inline-flex items-center px-6 py-3', {
  variants: {
    isActive: {
      true: 'pointer-events-none bg-gray-800 text-white',
      false: 'cursor-pointer hover:bg-gray-50 hover:text-gray-700',
    },
  },
  defaultVariants: {
    isActive: false,
  },
});

const LanguageTab = ({ item, isActive }: LanguageTabProps) => {
  return (
    <Link
      href={`/privacy-policy/${item.value}`}
      className={toggleItemVariants({
        isActive,
      })}
      aria-current={isActive ? 'page' : undefined}
      aria-label={`Switch to ${item.label}`}
      prefetch={!isActive}
      {...createDataAttributes({
        test: `language-tab-${item.value}`,
        active: isActive,
      })}
    >
      <span className="text-body-2 font-medium">{item.value}</span>
    </Link>
  );
};

export const LanguageToggle = ({ value }: LanguageToggleProps) => {
  return (
    <div
      role="group"
      aria-label={value === 'th' ? 'เปลี่ยนภาษาอังกฤษ' : 'Switch to Thai'}
      className={cn([
        'rounded-2xs text-button-1 font-noto-bold relative flex h-10.5 w-fit overflow-hidden text-gray-800 uppercase',
        'border border-gray-800',
      ])}
      {...createDataAttributes({
        test: 'language-toggle',
      })}
    >
      {LANGUAGES.map((item) => (
        <LanguageTab
          key={item.value}
          item={item}
          isActive={value === item.value}
        />
      ))}
    </div>
  );
};
